/**
 * Selectable Table Component
 *
 * An interactive table with a checkbox column. The user selects one or more
 * rows and clicks Apply; the selected rows are handed to `onApply`. Mirrors the
 * staged-selection + Apply/Cancel pattern of `CheckboxGroup`/`RadioGroup`, but
 * for tabular data — it composes the existing `DataTable` (client variant) so
 * search, sorting, pagination, and stable row selection all come for free.
 *
 * Inline tables can be cramped, so the card has an expand-to-dialog button
 * (like `DataTableWrapper`). The inline and dialog views are two separate
 * `DataTable` instances, so row selection is **controlled here** and shared
 * between them — toggling a checkbox in either view stays in sync.
 *
 * This component is generic over the row shape (`TData`) and intentionally
 * knows nothing about chat. The block renderer that wires it into chat maps the
 * selected rows into the outgoing UI action payload.
 */

import * as React from "react"
import { Maximize2 } from "lucide-react"
import {
  cn,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  type ClientDataTableProps,
} from "@strivelabs/ui-core"

/** The controlled row-selection map shape, derived from DataTable's own prop. */
type RowSelectionMap<TData> = NonNullable<ClientDataTableProps<TData>["rowSelection"]>

/**
 * Override the DataTable selection checkboxes to the black-on-white look used by
 * CheckboxGroup (the default Checkbox uses the primary/orange accent). Scoped to
 * this table only via arbitrary-variant selectors on the [role=checkbox] button —
 * the same targeting pattern DataTable itself uses for selection-column spacing.
 */
const CHECKBOX_THEME = cn(
  "[&_[role=checkbox]]:border-custom-border",
  // Checked: solid black box + white check (matches CheckboxGroup).
  "[&_[role=checkbox][data-state=checked]]:bg-custom-text-primary [&_[role=checkbox][data-state=checked]]:border-custom-text-primary [&_[role=checkbox][data-state=checked]]:text-white",
  // The header "select all" goes indeterminate when only some rows are selected.
  // Render it as a plain empty box (no fill, no glyph) so a partial selection
  // never looks like "all selected" — the header only fills in once every row
  // is selected (or the user ticks it explicitly).
  "[&_[role=checkbox][data-state=indeterminate]_svg]:hidden"
)

export interface SelectableTableProps<TData>
  extends Omit<
    ClientDataTableProps<TData>,
    "enableRowSelection" | "onSelectionChange" | "rowSelection" | "onRowSelectionChange"
  > {
  /** Called when the user clicks Apply; receives the currently selected rows. */
  onApply: (selectedRows: TData[]) => void
  /** Optional callback when the user clicks Cancel. */
  onCancel?: () => void
  /** Optional callback fired whenever the staged selection changes (before Apply). */
  onSelectionChange?: (selectedRows: TData[]) => void

  /** Heading rendered above the table. */
  title?: string
  /** Description text under the title. */
  description?: string

  /** Lock the table and actions (e.g. after submit, or in read-only history). */
  disabled?: boolean
  /** Minimum number of rows that must be selected before Apply is enabled. Default: 1 */
  minSelected?: number

  /** Text for the action buttons. */
  applyButtonText?: string
  cancelButtonText?: string
  /** Override the "{n} selected" summary rendered next to the actions. */
  renderSelectionSummary?: (count: number) => React.ReactNode

  /** Show the expand-to-dialog button. Default: true */
  expandable?: boolean
  /** Max-height applied to the table inside the expanded dialog. Default: '70vh' */
  expandedMaxHeight?: string

  /** Classes for the outer card wrapper. */
  wrapperClassName?: string
  /** Classes for the scrollable table container. */
  tableClassName?: string
}

export function SelectableTable<TData>({
  onApply,
  onCancel,
  onSelectionChange,
  title,
  description,
  disabled = false,
  minSelected = 1,
  applyButtonText = "Apply",
  cancelButtonText = "Cancel",
  renderSelectionSummary,
  expandable = true,
  expandedMaxHeight = "70vh",
  wrapperClassName,
  tableClassName,
  // Sensible chat-element defaults; any can be overridden by the caller.
  bordered = false,
  enableExport = false,
  // Pulled out so we can resolve selected rows + share selection across instances.
  data,
  getRowId,
  ...dataTableProps
}: SelectableTableProps<TData>) {
  // Selection is controlled here so the inline + expanded-dialog tables (two
  // separate DataTable instances) stay in sync.
  const [rowSelection, setRowSelection] = React.useState<RowSelectionMap<TData>>({})
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)

  // Mirror DataTable's getRowId default (index.toString()) so derived ids match.
  const resolveId = React.useCallback(
    (row: TData, index: number) => (getRowId ? getRowId(row, index) : String(index)),
    [getRowId]
  )

  const selectedRows = React.useMemo(
    () => data.filter((row, i) => rowSelection[resolveId(row, i)]),
    [data, rowSelection, resolveId]
  )

  React.useEffect(() => {
    onSelectionChange?.(selectedRows)
  }, [selectedRows, onSelectionChange])

  const count = selectedRows.length
  const canApply = !disabled && count >= minSelected
  const isEmpty = data.length === 0

  const handleApply = React.useCallback(() => {
    if (!canApply) return
    onApply(selectedRows)
    setIsDialogOpen(false)
  }, [canApply, onApply, selectedRows])

  const handleCancel = React.useCallback(() => {
    if (disabled) return
    onCancel?.()
    setIsDialogOpen(false)
  }, [disabled, onCancel])

  const renderTable = () => (
    <div
      className={cn(
        "w-full overflow-auto p-px",
        CHECKBOX_THEME,
        // Post-submit / history: lock the table visually and interactively.
        disabled && "pointer-events-none opacity-70",
        tableClassName
      )}
      aria-disabled={disabled}
    >
      <DataTable<TData>
        variant="client"
        data={data}
        getRowId={getRowId}
        enableRowSelection
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        bordered={bordered}
        enableExport={enableExport}
        {...dataTableProps}
      />
    </div>
  )

  const renderFooter = () => (
    <div className="flex items-center justify-between gap-2 mt-4">
      <span className="text-sm text-muted-foreground">
        {renderSelectionSummary ? renderSelectionSummary(count) : `${count} selected`}
      </span>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ai-secondary"
          size="xs"
          onClick={handleCancel}
          disabled={disabled}
        >
          {cancelButtonText}
        </Button>
        <Button
          type="button"
          variant="ai-primary"
          size="xs"
          onClick={handleApply}
          disabled={!canApply}
        >
          {applyButtonText}
        </Button>
      </div>
    </div>
  )

  return (
    <>
      <div
        className={cn(
          "bg-ai-element border-half border-custom-border rounded-lg p-5 relative",
          wrapperClassName
        )}
      >
        {expandable && !isEmpty && (
          <Button
            variant="ghost"
            size="iconSm"
            onClick={() => setIsDialogOpen(true)}
            className="absolute top-3 right-3 z-10 bg-custom-card-bg/60 border border-custom-border shadow-sm hover:bg-custom-card-bg/80 cursor-pointer"
            aria-label="Expand table"
          >
            <Maximize2 className="size-3.5 text-muted-foreground" />
          </Button>
        )}

        {title && (
          <p className="mb-1 text-base font-medium text-black pr-8">{title}</p>
        )}
        {description && (
          <p className="mb-4 text-sm text-muted-foreground pr-8">{description}</p>
        )}

        {renderTable()}
        {renderFooter()}
      </div>

      {/* Expanded view — a second DataTable instance bound to the same controlled selection. */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-7xl w-[95vw] max-h-[90vh] overflow-auto">
          <DialogHeader>
            {title && <DialogTitle>{title}</DialogTitle>}
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
          <div className="mt-4 flex flex-col" style={{ maxHeight: expandedMaxHeight }}>
            {renderTable()}
            {renderFooter()}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

SelectableTable.displayName = "SelectableTable"
